import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType, EffectNotification, OnRunEffects } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';

import { combineLatest, merge, Observable, of, timer, exhaustMap, filter, map, skipUntil, switchMap } from 'rxjs';
import * as UserActions from '../actions/user.actions';
import * as SchedulesActions from '../actions/schedules.actions';

import { Store } from '@ngrx/store';

import { allResources, currentUser } from '../selectors';
import { favouriteAdded, favouriteRemoved } from '../actions/resources.actions';
import { loadFavourites } from '../actions/schedules.actions';
import { add } from 'date-fns';
import { ApiService, favourireIds } from '@cue/api';
import { DataService } from '../services/data.service';
import { AppState } from '../models';
import { getNextQuarter } from '@cue/data';

@Injectable()
export class FavouritesEffects implements OnRunEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<AppState>>(Store<AppState>);
  private apiService = inject(ApiService);
  private dataService = inject(DataService);
  // Remove after server
  refreshFavouritesEvery10Secs$ = createEffect(() =>
    merge(timer(500, 15000), this.actions$.pipe(ofType(favouriteAdded, favouriteRemoved))).pipe(
      concatLatestFrom(() => this.store.select(currentUser)),
      filter(([_, user]) => user?.data != null),
      map(loadFavourites)
    )
  );

  loadFavourites$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SchedulesActions.loadFavourites),
      switchMap((action) =>
        combineLatest([
          this.apiService.call(favourireIds()),
          of(action),
          this.store.select(allResources).pipe(filter((x) => !x.loading && x.data!.length > 0)),
        ])
      ),
      exhaustMap(([favouriteIds, action, packedResources]) => {
        if (favouriteIds.length === 0) {
          return of(
            SchedulesActions.favouritesLoaded({
              schedules: [],
              start: new Date(),
            })
          );
        }
        const favouriteResources = packedResources.data!.filter(
          (x) => favouriteIds.find((a) => a.toUpperCase() === x.id.toUpperCase()) != null
        );
        const newNow = getNextQuarter(new Date());
        return this.dataService
          .getSchedulesForResources(
            [
              {
                requirement: {
                  id: 'favourites',
                } as any,
                resources: favouriteResources,
                additional: false,
              },
            ],
            newNow,
            add(new Date(), {
              days: 10,
            })
          )
          .pipe(
            map((response) => {
              return SchedulesActions.favouritesLoaded({
                schedules: response[0] ? response[0] : [],
                start: newNow,
              });
            })
          );
      })
    )
  );

  ngrxOnRunEffects(resolvedEffects$: Observable<EffectNotification>) {
    return resolvedEffects$.pipe(skipUntil(this.actions$.pipe(ofType(UserActions.userLoaded))));
  }
}
